  /*
  * Inverter telemetry resume
  BN: 'batteryCyclesCount',
  BP: 'batterySoc',
  BP1: 'FirstBatterySoc',
  BP2: 'SecondBatterySoc',
  EG1: 'dailyGeneratedEnergy',
  EG2: 'dailyExportedEnergy',
  EG3: 'dailyImportedEnergy',
  EG4: 'dailyConsumedEnergy',
  EG5: 'dailyChargedEnergy',
  EG6: 'dailyDischargedEnergy',
  ET1: 'totalGeneratedEnergy',
  ET2: 'totalExportedEnergy',
  ET3: 'totalImportedEnergy',
  ET4: 'totalConsumedEnergy',
  ET5: 'totalChargedEnergy',
  ET6: 'totalDischargedEnergy',
  GwId: 'connextId',
  MP1: 'powerDC1',
  MP2: 'powerDC2',
  P1: 'powerGenerating',
  P2: 'powerExporting',
  P3: 'powerImporting',
  P4: 'powerConsuming',
  P5: 'powerCharging',
  P6: 'powerDischarging',
  P7: 'powerChargingTwo',
  P8: 'powerDischargingTwo',
  P9: 'powerGeneratingExtern',
  TS: 'timestamp' */

import { Inverter } from "../telemetry";

export interface SystemEnergyResume {
  ts: number;
  solarEnergy: number;
  toBattery: number;
  toGrid: number;
  toHome: number;
  homeEnergy: number;
  fromBattery: number;
  fromGrid: number;
  fromSolar: number;
}

export interface SystemPowerState {
  solar: number;
  home: number;
  grid: number;
  battery: number;
  batteryPower: number;
  fromSolarToHome: number;
  fromSolarToBattery: number;
  fromSolarToGrid: number;
  fromGridToHome: number;
  fromGridToBattery: number;
  fromBatteryToHome: number;
  fromBatteryToGrid: number;
  ts: number;
  solarimeter?: {
    irradiance?: number;
    panelTemperature?: number;
  };
}

export enum PowerFlowGraphMode {
  OnlyGeneratedPower = 0,
  WithImportExport = 1,
  WithBattery = 2,
}

export enum EvchargeTelemetryState {
  Start = 0,
  Error = 1,
  Idle = 2,
  Charging = 3,
  Parking = 4,
}

export enum EvchargeStrategy {
  Fast = 0,
  Green = 1,
  Km_repeat = 2,
  Green_mixed = 3,
  Km_single = 4,
  Time_power = 5,
  Time_power_single = 6,
  Zero_emissions = 7,
}

/**
 * Available -> When a Connector becomes available for a new user (Operative)
 * Preparing -> When a Connector becomes no longer available for a new user but no charging session is active.
 *  Typically a Connector is occupied when a user presents a tag, inserts a cable or a vehicle occupies the parking bay (Operative)
 * Charging -> When the contactor of a Connector closes, allowing the vehicle to charge (Operative)
 * SuspendedEVSE -> When the contactor of a Connector opens upon request of the EVSE,
 *  e.g. due to a smart charging restriction or as the result of
 *  StartTransaction.conf indicating that charging is not allowed (Operative)
 * SuspendedEV -> When the EVSE is ready to deliver energy but contactor is open, e.g. the EV is not ready.
 * Finishing -> When a charging session has stopped at a Connector, but the Connector is not yet available for a new user,
 *  e.g. the cable has not been removed or the vehicle has not left the parking bay (Operative)
 * Reserved -> When a Connector becomes reserved as a result of a Reserve Now command (Operative)
 * Faulted -> When a Charge Point or connector has reported an error and is not available for energy delivery (Inoperative).
 * Unavailable -> When a Connector becomes unavailable as the result of a Change Availability command or
 *  an event upon which the Charge Point transitions to unavailable at its discretion. Upon receipt of a
 *  Change Availability command, the status MAY change immediately or the change MAY be scheduled. When scheduled, the Status
 *  Notification shall be send when the availability change becomes effective (Inoperative)
 */

export enum EvchargeConfigState {
  Available = 1,
  Preparing = 2,
  Charging = 3,
  SuspendedEVSE = 4, // Here we can apply start / stop charging
  SuspendedEV = 5, // Here we can not apply start / stop charging
  Finishing = 6,
  Reserved = 7,
  Unavailable = 8,
  Faulted = 9
}

export type TelemetryStatus = {
  someDeviceWithoutTelemetry?: boolean;
  checked?: boolean | null;
  message?: string | null;
};

export const DEFAULT_TELEMETRY_REFRESH_INTERVAL = 1000 * 60 * 2 + 1000 * 30; // 2.5 minutes

export interface SystemEnergyState {
  energyDailyGenerating: number;
  energyDailyConsuming: number;
  energyDailyCharging: number;
  energyDailyDischarging: number;
  energyDailyImporting: number;
  energyDailyExporting: number;
  energyTotalGenerating: number;
  energyTotalConsuming: number;
  energyTotalCharging: number;
  energyTotalDischarging: number;
  energyTotalImporting: number;
  energyTotalExporting: number;

  EG1: number;
  EG4: number;
  EG5: number;
  EG6: number;
  EG3: number;
  EG2: number;
  ET1: number;
  ET4: number;
  ET5: number;
  ET6: number;
  ET3: number;
  ET2: number;

  SOL_EG?: number;
  energyDailyIrradiation?: number;

  ts: number;
}

export interface SystemBatteriesPercentageState{
  deviceId?: string;
  BP1?: string;
  BP2?: string;
  BP?: string;
}

export interface CurrentEnergyPowerSystemState {
  powerState?: SystemPowerState;
  energyState?: SystemEnergyState;
  connectionStatus?: TelemetryStatus;
  processedData?: { [key: string]: Partial<Inverter> };
}

export interface CurrentPowerSystemState {
  powerState: SystemPowerState;
  processedData?: { [key: string]: Partial<Inverter> };
}

export type GraphTelemetryMode = 'asDays' | 'asWeeks' | 'asMonths';
